/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useState, useEffect, useContext } from 'react';
import styles from './styles.module.css';
import Header from './components/Header';
import StatusAndInformation from './scenes/StatusAndInformation';
import { Route } from 'react-router-dom';
import axios from 'axios';
import { handleApiError } from '../../../../services/utils/errorHandler';
import ConfigContext from '../../../../components/ConfigContext';
import { Spin } from 'antd';
import Logs from './scenes/Log';
import PolicyCompliance from './scenes/PolicyCompliance';
import { RoutesContext } from '../../index';
import { useTranslation } from 'react-i18next';
import WindowsDefender from './scenes/WindowsDefender';
import RemoteSession from './scenes/RemoteSession';
import Apps from './scenes/Apps';
import Accounts from './scenes/Accounts';

export const DeviceDetailsContext = React.createContext();
const DeviceDetailsProvider = DeviceDetailsContext.Provider;

const SingleDevice = props => {
  const { deviceIdentifier, deviceType } = props.match.params;
  const { config } = useContext(ConfigContext);
  const routesContext = useContext(RoutesContext);
  const { t } = useTranslation();
  const [currentRoute, setCurrentRoute] = useState('info');
  const [device, setDevice] = useState(null);

  let routes;

  if (deviceType === 'windows') {
    routes = [
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/info`,
        component: StatusAndInformation,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/log`,
        component: Logs,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/policy-compliance`,
        component: PolicyCompliance,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/windows-defender`,
        component: WindowsDefender,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/apps`,
        component: Apps,
        exact: true,
      },
    ];
  } else {
    routes = [
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/info`,
        component: StatusAndInformation,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/log`,
        component: Logs,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/policy-compliance`,
        component: PolicyCompliance,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/remote-session`,
        component: RemoteSession,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/apps`,
        component: Apps,
        exact: true,
      },
      {
        path: `/${config.appName}/devices/:deviceType/:deviceIdentifier/accounts`,
        component: Accounts,
        exact: true,
      },
    ];
  }

  const deviceDetails = {
    deviceIdentifier,
    deviceType,
    device,
    currentRoute,
    setCurrentRoute,
  };

  // Store interval id of fetchDevice interval
  let intervalId;

  useEffect(() => {
    fetchDevice();
    if (config.refreshSingleDeviceView) {
      intervalId = setInterval(
        fetchDevice,
        config.singleDeviceViewRefreshInterval,
      );
    }
    routesContext.setCurrentRoute('');
    return () => {
      // Clear interval when unmount
      clearInterval(intervalId);
    };
  }, []);

  const fetchDevice = () => {
    axios
      .get(
        window.location.origin +
          config.serverConfig.invoker.uri +
          config.serverConfig.invoker.deviceMgt +
          `/devices/${deviceType}/${deviceIdentifier}`,
      )
      .then(result => {
        if (result.status === 200) {
          setDevice(result.data.data);
        } else {
          throw new Error();
        }
      })
      .catch(error => {
        handleApiError(error, t('api_retrieveDeviceDetailsError'), t);
      });
  };

  // Display a loading animation if the device is null
  if (device == null) {
    return (
      <Spin className={styles.spinner} size="large" tip={t('label_loading')} />
    );
  }

  return (
    <div>
      <DeviceDetailsProvider value={deviceDetails}>
        <Header
          callback={() => {
            fetchDevice();
          }}
        />
        <div className={styles.content}>
          {routes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              exact={route.exact}
              component={route.component}
            />
          ))}
        </div>
      </DeviceDetailsProvider>
    </div>
  );
};

export default SingleDevice;
